import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';

import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import BundlePropertyPicker, {
  PropertyOption,
} from '~/components/Accommodation/Pages/Bundles/components/BundlePropertyPicker';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { useCreateCampaign } from '~/hooks/apis/accommodation/useCampaigns';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CreateCampaignPayload } from '~/services/AccommodationService';

import CampaignBreadcrumbs from './components/CampaignBreadcrumbs';
import CampaignOccupancyEditor, { CampaignDefaultOccupancy } from './components/CampaignOccupancyEditor';
import CampaignScheduleEditor, {
  CampaignScheduleRow,
  sanitizeSchedulesForSubmit,
} from './components/CampaignScheduleEditor';
import { parseStrictInteger } from './components/campaignNumberInput';
import { ALL_CAMPAIGN_STATUSES } from './constants';

const DEFAULT_OCCUPANCY: CampaignDefaultOccupancy = { adults: 2 };

interface FormState {
  name: string;
  displayName: string;
  subtitle: string;
  status: NonNullable<CreateCampaignPayload['status']>;
  properties: Array<PropertyOption>;
  schedules: Array<CampaignScheduleRow>;
  marketedLos: string;
  defaultOccupancy: CampaignDefaultOccupancy | null;
}

const INITIAL_STATE: FormState = {
  name: '',
  displayName: '',
  subtitle: '',
  status: 'draft',
  properties: [],
  schedules: [],
  marketedLos: '',
  defaultOccupancy: null,
};

function AccommodationCampaignCreatePage() {
  const { push } = useAppNavigation();
  const [state, setState] = useState<FormState>(INITIAL_STATE);

  const createCampaignMutation = useCreateCampaign();

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      const payload: CreateCampaignPayload = {
        name: state.name,
        displayName: state.displayName.trim() || undefined,
        subtitle: state.subtitle.trim() || undefined,
        status: state.status,
        propertyIds: state.properties.map((p) => p.id),
        schedules: sanitizeSchedulesForSubmit(state.schedules),
        marketedLos: parseStrictInteger(state.marketedLos, 1),
        defaultOccupancy: state.defaultOccupancy ?? undefined,
      };

      try {
        const result = await createCampaignMutation.mutateAsync({ payload });
        push(`/accommodation/campaigns/${result.campaignId}`);
      } catch {
        // Errors are surfaced via the hook's snackbar.
      }
    },
    [state, createCampaignMutation, push],
  );

  const isSubmitting = createCampaignMutation.isPending;
  const canSubmit = !!state.name.trim() && state.properties.length > 0;

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>Create Campaign</title>
      </Helmet>
      <CampaignBreadcrumbs currentPage="Create Campaign" />
      <PageHeader title="Create Campaign" />

      <Box component="form" onSubmit={handleSubmit} mt={2}>
        <Stack spacing={2}>
          <TextField
            label="Campaign Name"
            helperText="Internal identifier — not shown to customers"
            value={state.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Display Name"
            helperText="Customer-facing name. Leave blank to fall back to the campaign name"
            value={state.displayName}
            onChange={(e) => handleChange('displayName', e.target.value)}
            fullWidth
          />

          <TextField
            label="Subtitle"
            helperText="Subject line / preheader copy"
            value={state.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={state.status}
              onChange={(e) => handleChange('status', e.target.value as FormState['status'])}
            >
              {ALL_CAMPAIGN_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CampaignScheduleEditor value={state.schedules} onChange={(next) => handleChange('schedules', next)} />

          <TextField
            label="Marketed Length of Stay (nights)"
            helperText="Informational — not an eligibility restriction. Leave blank to fall back to 1 night"
            type="number"
            inputProps={{ min: 1 }}
            value={state.marketedLos}
            onChange={(e) => handleChange('marketedLos', e.target.value)}
            sx={{ width: 280 }}
          />

          <Box border={1} borderColor="divider" borderRadius={1} p={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.defaultOccupancy !== null}
                  onChange={(e) => handleChange('defaultOccupancy', e.target.checked ? DEFAULT_OCCUPANCY : null)}
                />
              }
              label="Set a custom default occupancy"
            />
            {state.defaultOccupancy !== null && (
              <Box mt={1}>
                <CampaignOccupancyEditor
                  value={state.defaultOccupancy}
                  onChange={(next) => handleChange('defaultOccupancy', next)}
                />
              </Box>
            )}
          </Box>

          <BundlePropertyPicker value={state.properties} onChange={(next) => handleChange('properties', next)} />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => push('/accommodation/campaigns')}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Campaign'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}

export default AccommodationCampaignCreatePage;
